import { NgModule } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { GravatarModule } from 'ngx-gravatar'
import { ImgFallbackModule } from 'ngx-img-fallback'
import {
  NgbDropdownModule, NgbModalModule, NgbProgressbarModule,
  NgbPaginationModule, NgbTypeaheadModule,
} from '@ng-bootstrap/ng-bootstrap'

import { AvatarComponent } from './components/avatar.component'
import { GroupAvatarComponent } from './components/groupAvatar.component'
import { LocaleModule } from 'common/locale/module'
import { CheckboxComponent } from './components/checkbox.component'
import { ToggleComponent } from './components/toggle.component'
import { MessageBoxModalComponent } from './components/messageBoxModal.component'
import { SpinnerComponent } from './components/spinner.component'
import { SpinnerIconComponent } from './components/spinnerIcon.component'
import { EmptyStateComponent } from './components/emptyState.component'
import { ExternalShareUriInputComponent } from './components/externalShareUriInput.component'
import { SmartProgressComponent } from './components/smartProgress.component'
import { SmartRadialProgressComponent } from './components/smartRadialProgress.component'
import { BytesInputComponent } from './components/bytesInput.component'
import { FilterboxComponent } from './components/filterbox.component'
import { FloatingToolbarComponent } from './components/floatingToolbar.component'
import { SaveButtonComponent } from './components/saveButton.component'
import { PathSelectorComponent } from './components/pathSelector.component'
import { BetterSelectComponent } from './components/betterSelect.component'
import { AddObjectFormComponent } from './components/addObjectForm.component'
import { DeleteConfirmationModalComponent } from './components/deleteConfirmationModal.component'
import { MessageComponent } from './components/message.component'
import { DangerZoneModalComponent } from './components/dangerZoneModal.component'
import { ErrorModalComponent } from './components/errorModal.component'

import { PermissionEnableDirective } from './directives/permissionEnable.directive'
import { PreselectDirective } from './directives/preselect.directive'
import { ClickBlockingDirective } from './directives/clickBlocking.directive'
import { SRLabelDirective } from './directives/label.directive'
import { SearchHighlightDirective } from './directives/searchHighlight.directive'
import { AutofocusDirective } from './directives/autofocus.directive'
import { MatContextMenuTrigger } from './directives/matContextMenuTrigger.directive'
import { PermissionShowDirective } from './directives/permissionShow.directive'
import { AutoselectDirective } from './directives/autoselect.directive'
import { CopyToClipboardButtonDirective } from './directives/copyToClipboardButton.directive'
import { ExpirableDirective } from './directives/expirable.directive'
import { ActiveLinkDirective } from './directives/activeLink.directive'
import { NonEmptyValidatorDirective } from './directives/nonEmpty.directive'
import { FlowButtonDirective } from './directives/flowButton.directive'
import { BrokenImageDirective } from './directives/brokenImage.directive'
import { TextAreaTypeaheadDirective } from './directives/textAreaTypeahead.directive'

import { NaturalOrderByPipe } from './pipes/naturalOrderBy.pipe'
import { BytesPipe } from './pipes/bytes.pipe'
import { TimePipe } from './pipes/time.pipe'

import { DatabaseProfilerService } from './services/databaseProfiler.service'

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    GravatarModule,
    LocaleModule.forChild(),
    NgbDropdownModule,
    NgbModalModule,
    NgbProgressbarModule,
    NgbPaginationModule,
    NgbTypeaheadModule,
    ImgFallbackModule,
  ],
  declarations: [
    ActiveLinkDirective,
    AddObjectFormComponent,
    AutofocusDirective,
    AutoselectDirective,
    AvatarComponent,
    GroupAvatarComponent,
    BetterSelectComponent,
    BrokenImageDirective,
    BytesInputComponent,
    BytesPipe,
    CheckboxComponent,
    ClickBlockingDirective,
    CopyToClipboardButtonDirective,
    DangerZoneModalComponent,
    DeleteConfirmationModalComponent,
    EmptyStateComponent,
    ErrorModalComponent,
    ExternalShareUriInputComponent,
    ExpirableDirective,
    FilterboxComponent,
    FloatingToolbarComponent,
    FlowButtonDirective,
    MatContextMenuTrigger,
    MessageComponent,
    MessageBoxModalComponent,
    NaturalOrderByPipe,
    NonEmptyValidatorDirective,
    PermissionEnableDirective,
    PermissionShowDirective,
    PreselectDirective,
    SaveButtonComponent,
    PathSelectorComponent,
    SearchHighlightDirective,
    SmartProgressComponent,
    SmartRadialProgressComponent,
    SpinnerComponent,
    SpinnerIconComponent,
    SRLabelDirective,
    TextAreaTypeaheadDirective,
    TimePipe,
    ToggleComponent,
  ],
  exports: [
    ActiveLinkDirective,
    AddObjectFormComponent,
    AutofocusDirective,
    AutoselectDirective,
    AvatarComponent,
    GroupAvatarComponent,
    BetterSelectComponent,
    BrokenImageDirective,
    BytesInputComponent,
    BytesPipe,
    CheckboxComponent,
    ClickBlockingDirective,
    CopyToClipboardButtonDirective,
    DangerZoneModalComponent,
    DeleteConfirmationModalComponent,
    EmptyStateComponent,
    ErrorModalComponent,
    ExpirableDirective,
    FilterboxComponent,
    FloatingToolbarComponent,
    FlowButtonDirective,
    MatContextMenuTrigger,
    MessageComponent,
    NaturalOrderByPipe,
    NonEmptyValidatorDirective,
    PermissionEnableDirective,
    PermissionShowDirective,
    PreselectDirective,
    SaveButtonComponent,
    PathSelectorComponent,
    SearchHighlightDirective,
    SmartProgressComponent,
    SmartRadialProgressComponent,
    SpinnerComponent,
    SpinnerIconComponent,
    SRLabelDirective,
    TextAreaTypeaheadDirective,
    TimePipe,
    ToggleComponent,
  ],
})
export class UtilCommonModule {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor, @typescript-eslint/no-empty-function
  constructor (_databaseProfiler: DatabaseProfilerService) { }
}

export { LicenseService } from './services/license.service'
export { PageTitleService } from './services/pageTitle.service'
export { SelectionService, Selection } from './services/selection.service'
export { SharedStorageService, UserStorageService } from './services/sharedStorage.service'
export { SocketService, SocketIOFactory } from './services/socket.service'
export { MessageBoxService, MessageBox } from './services/messageBox.service'
export { HotkeysService } from './services/hotkeys.service'
export { SecretService } from './services/secret.service'
export { BaseClipboardService } from './services/baseClipboardService'
export { RouteLoggerService } from './services/routeLogger.service'
export { NotifyService } from './services/notify.service'

export {
  NaturalOrderByPipe,
  BytesPipe,
  DeleteConfirmationModalComponent,
  DangerZoneModalComponent,
  ErrorModalComponent,
}
